import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  TextField,
} from "@mui/material";
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  AutoAwesome as AutoAwesomeIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  InfoOutlined as InfoOutlinedIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import InfoIcon from "@mui/icons-material/Info";
import { myToast } from "../../libs";
import { useSelector } from "react-redux";
import { apiGetAllAgents, apiUpdateAgent } from "../../graphQL/agent";
import { apiGetAllAIEngines } from "../../graphQL/aiEngine";
import { apiCreateFile, apiGetFiles, apiGetFile } from "../../graphQL/file";
import {
  apiCreateAiRequest,
  apiExecuteAiRequestV2,
  apiGetAiRequests,
  aiSparkAiRequestBuilder,
} from "../../graphQL/aiRequest";
import { buildAiContent } from "../../utils/aiContentBuilders";
import { SparkUtilRequestBuilder } from "../../utils/SparkUtilRequestBuilder";
import { v4 as uuid } from "../../libs/uuid";
import moment from "../../libs/moment";  
// Import tab components
import { ViewTab, SummaryTab, MetadataTab, OutputTab, InfoTab } from "./tabs";
import { isTextFile, isPDF } from "./utils/fileHelpers";

const FILE_SUMMARY_AGENT_VERSION = "YRH_FILE_SUMMARY_V2";

const FILE_SUMMARY_SYSTEM_PREFERENCES = `${FILE_SUMMARY_AGENT_VERSION}
You are the FileSummary agent.
Focus only on the provided file content and context.
Identify what the file is, then extract the most important business-relevant data from it.
Do not invent details. If data is missing, return an empty string or empty array.
Keep output factual, concise, and structured for quick review.`;

const FILE_SUMMARY_DEFAULT_PROMPT = `Analyze the provided file and return:
1) what the file is (document type/purpose),
2) a concise summary,
3) the most important data points a user should know.
Prioritize concrete facts, numbers, names, dates, and identifiers.
Use only information present in the file/context.`;

const FILE_SUMMARY_INPUT_SCHEMA = {
  file: {
    id: "{{file.id}}",
    name: "{{file.name}}",
    description: "{{file.description}}",
    type: "{{file.type}}",
    fileCategory: "{{file.fileCategory}}",
    mimeType: "{{file.mimeType}}",
    size: "{{file.size}}",
    url: "{{file.url}}",
    publicUrl: "{{file.publicUrl}}",
    summary: "{{file.summary}}",
    data: "{{file.data}}",
  },
  property: {
    id: "{{property.id}}",
    name: "{{property.name}}",
    identity: "{{property.identity}}",
  },
  contact: {
    id: "{{contact.id}}",
    identity: "{{contact.identity}}",
  },
};

const FILE_SUMMARY_OUTPUT_SCHEMA = {
  type: "object",
  additionalProperties: false,
  properties: {
    fileType: { type: "string" },
    summary: { type: "string" },
    importantData: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        properties: {
          label: { type: "string" },
          value: { type: "string" },
          whyItMatters: { type: "string" },
          source: { type: "string" },
        },
        required: ["label", "value", "whyItMatters", "source"],
      },
    },
    keyRisks: {
      type: "array",
      items: { type: "string" },
    },
    recommendedFollowUps: {
      type: "array",
      items: { type: "string" },
    },
  },
  required: [
    "fileType",
    "summary",
    "importantData",
    "keyRisks",
    "recommendedFollowUps",
  ],
};

const PreviewFile = ({
  open,
  file,
  propertyName,
  identity,
  agents = [],
  loadingAgents = false,
  onClose,
  onDownload,
  onSaveSummary,
  isDomainFile = false,
  propertyId,
  contactId,
  initialTab = 0,
  autoGenerateSummary = false,
}) => {
  const [tab, setTab] = useState(0);

  const [backendFileDoc, setBackendFileDoc] = useState(null);
  const [backendDocLoading, setBackendDocLoading] = useState(false);
  // Error kept in state for the console trace only; not rendered anywhere yet.
  const [, setBackendDocError] = useState("");
  const [backendDocFetchedAt, setBackendDocFetchedAt] = useState(null);

  // Check if file is a system/domain file (read-only)
  const isSystemFile = file?.isDomainFile === true;

  // View tab state
  const [fileContent, setFileContent] = useState("");
  const [loadingFileContent, setLoadingFileContent] = useState(false);

  // Summary tab state
  const [summaryText, setSummaryText] = useState("");
  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const [promptText, setPromptText] = useState("");
  const [isFullScreenPrompt, setIsFullScreenPrompt] = useState(false);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [showAiRequestPreview, setShowAiRequestPreview] = useState(false);
  const [autoSummaryRunKey, setAutoSummaryRunKey] = useState("");

  // Metadata tab state
  const [metadataPairs, setMetadataPairs] = useState([]);
  const [isEditingMetadata, setIsEditingMetadata] = useState(false);
  const [extractedMetadata, setExtractedMetadata] = useState("");
  const [metadataPromptText, setMetadataPromptText] = useState("");
  const [isGeneratingMetadata, setIsGeneratingMetadata] = useState(false);

  // Output tab state
  const [selectedOutputAgentId, setSelectedOutputAgentId] = useState("");
  const [outputResponse, setOutputResponse] = useState(null);
  const [outputDownloadedFiles, setOutputDownloadedFiles] = useState([]);
  const [isExecutingOutput, setIsExecutingOutput] = useState(false);
  const [isEditingOutputPrompt, setIsEditingOutputPrompt] = useState(false);
  const [outputPromptText, setOutputPromptText] = useState("");

  // Shared state
  const [currentRequestFileId, setCurrentRequestFileId] = useState(null);
  const [aiRequestDebug, setAiRequestDebug] = useState({});

  // Public agents state (fetched directly, not from Redux)
  const [publicAgents, setPublicAgents] = useState([]);
  const [loadingPublicAgents, setLoadingPublicAgents] = useState(false);

  const PrivateInfo = useSelector((store) => store.auth.PrivateInfo);

  const isSuperAdminRole = (() => {
    const r = String(PrivateInfo?.role || "").trim().toLowerCase();
    return r === "sysadmin" || r === "superadmin";
  })();

  let nextTabIndex = 1;
  const SUMMARY_TAB = !isSystemFile ? nextTabIndex++ : -1;
  const METADATA_TAB = !isSystemFile ? nextTabIndex++ : -1;
  const OUTPUT_TAB = !isSystemFile ? nextTabIndex++ : -1;
  const JSON_TAB = isSuperAdminRole && !isSystemFile ? nextTabIndex++ : -1;
  const INFO_TAB = isDomainFile ? nextTabIndex++ : -1;

  const refetchBackendFileDoc = async () => {
    if (!file) return;

    setBackendDocLoading(true);
    setBackendDocError("");

    try {
      let fileDocId = file.fileDocumentId;
      let aiFileId = file.aiFileId;

      if (!fileDocId) {
        const result = await ensureAiFileId(file);
        fileDocId = result.fileDocId;
        aiFileId = result.aiFileId;
      }

      if (!fileDocId)
        throw new Error("No backend fileDocumentId found/created.");

      const doc = await apiGetFile(file.id, file.authorId);

      setBackendFileDoc(doc || null);
      setBackendDocFetchedAt(new Date().toISOString());
    } catch (err) {
      console.error("Refetch backend file doc failed:", err);
      setBackendFileDoc(null);
      setBackendDocError(
        err?.message || "Failed to fetch backend file document"
      );
    } finally {
      setBackendDocLoading(false);
    }
  };
  useEffect(() => {
    if (!open) {
      setBackendFileDoc(null);
      setBackendDocError("");
      setBackendDocLoading(false);
      setBackendDocFetchedAt(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (tab === JSON_TAB) refetchBackendFileDoc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab, file?.id]);

  // Fetch public agents when dialog opens
  useEffect(() => {
    const fetchPublicAgents = async () => {
      if (!open) return;

      setLoadingPublicAgents(true);
      try {
        const response = await apiGetAllAgents({ isPublic: true });
        const agents = response.agents || [];
        const engineIds = Array.from(
          new Set(agents.map((a) => a?.engineId).filter(Boolean))
        );

        if (engineIds.length === 0) {
          setPublicAgents(agents);
          return;
        }

        const enginesResponse = await apiGetAllAIEngines({ ids: engineIds });
        const engines = enginesResponse?.aiEngines || [];
        const engineById = engines.reduce((acc, engine) => {
          acc[engine.id] = engine;
          return acc;
        }, {});

        const enrichedAgents = agents.map((agent) => {
          const engine = agent?.engineId ? engineById[agent.engineId] : null;
          return {
            ...agent,
            engine,
            model: engine?.model || agent?.model || null,
          };
        });

        setPublicAgents(enrichedAgents);
      } catch (error) {
        console.error("Error fetching public agents:", error);
        setPublicAgents([]);
      } finally {
        setLoadingPublicAgents(false);
      }
    };

    fetchPublicAgents();
  }, [open]);

  // Initialize fields from file data when dialog opens or file changes
  useEffect(() => {
    if (open && file) {
      setSummaryText(file.summary || "");
      setPromptText(file.prompt || file.customPrompt || "");
      setExtractedMetadata(file.extractedMetadata || "");
      setMetadataPromptText(file.metadataPrompt || "");
      setIsEditingSummary(false);
      setIsEditingMetadata(false);
    }
  }, [open, file?.id, file?.summary, file?.prompt, file?.extractedMetadata]);

  useEffect(() => {
    if (!open || !file) return;
    if (initialTab === SUMMARY_TAB && SUMMARY_TAB >= 0) {
      setTab(SUMMARY_TAB);
    } else {
      setTab(0);
    }
  }, [open, file?.id, initialTab, SUMMARY_TAB]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setIsGeneratingSummary(false);
      setIsGeneratingMetadata(false);
      setCurrentRequestFileId(null);
      setTab(0);
      setFileContent("");
      setLoadingFileContent(false);
      setSelectedOutputAgentId("");
      setOutputResponse(null);
      setOutputDownloadedFiles([]);
      setIsExecutingOutput(false);
      setIsEditingOutputPrompt(false);
      setOutputPromptText("");
      setShowAiRequestPreview(false);
      setAutoSummaryRunKey("");
    }
  }, [open]);

  useEffect(() => {
    if (!open || !autoGenerateSummary || SUMMARY_TAB < 0 || tab !== SUMMARY_TAB) {
      return;
    }
    if (!file?.id || isGeneratingSummary) return;

    const nextRunKey = `${file.id}:${tab}:${autoGenerateSummary ? "1" : "0"}`;
    if (autoSummaryRunKey === nextRunKey) return;

    setAutoSummaryRunKey(nextRunKey);
    handleGenerateSummary();
  }, [
    open,
    autoGenerateSummary,
    SUMMARY_TAB,
    tab,
    file?.id,
    isGeneratingSummary,
    autoSummaryRunKey,
  ]);

  useEffect(() => {
    if (tab !== 1) setShowAiRequestPreview(false);
  }, [tab]);


  // Fetch text file content for View tab
  useEffect(() => {
    if (file && isTextFile(file)) {
      const resolvedUrl = file?.isPublic && file?.publicUrl ? file.publicUrl : file.url;
      fetchFileContent(resolvedUrl);
    } else {
      setFileContent("");
      setLoadingFileContent(false);
    }
  }, [file]);

  // Initialize metadata pairs
  useEffect(() => {
    if (!open) return;
    setMetadataPairs(Array.isArray(file?.metadata) ? file.metadata : []);
    setIsEditingMetadata(false);
  }, [open, file]);
  

  const bottomFileName = useMemo(() => {
    return file?.originalName || file?.name || file?.description || "";
  }, [file]);


  // ============================================================================
  // Helper Functions
  // ============================================================================

  const fetchFileContent = async (url) => {
    setLoadingFileContent(true);
    try {
      const response = await fetch(url);
      const text = await response.text();
      setFileContent(text);
    } catch (error) {
      console.error("Failed to fetch file content:", error);
      setFileContent("Error loading file content.");
    } finally {
      setLoadingFileContent(false);
    }
  };

  /**
   * Ensure file has File document ID for AI upload
   * Returns { fileDocId, aiFileId }
   */
  const ensureAiFileId = async (fileObj) => {
    if (fileObj.fileDocumentId) {
      return {
        fileDocId: fileObj.fileDocumentId,
        aiFileId: fileObj.aiFileId || null,
      };
    }

    try {
      const fileDoc = await apiCreateFile({
        authorId: PrivateInfo?.id || "unknown",
        fileCategory: "analysis",
        type: fileObj.type || "document",
        mimeType: fileObj.mimeType || "application/octet-stream",
        url: fileObj.url,
        key: fileObj.key || "",
        size: String(fileObj.size || 0),
        status: "active",
      });

      const fileDocId = fileDoc.id;
      const aiFileId = fileDoc.aiFileId || null;

      if (fileDocId && onSaveSummary) {
        onSaveSummary({
          summary: summaryText,
          extractedMetadata: extractedMetadata,
          prompt: promptText,
          metadataPrompt: metadataPromptText,
          fileDocumentId: fileDocId,
          aiFileID: aiFileId || "",
          fileId: fileObj.id,
        });
      }

      return { fileDocId, aiFileId };
    } catch (error) {
      console.error("Error creating file document:", error);
      throw new Error("Failed to create file document: " + error.message);
    }
  };

  // ============================================================================
  // AI Request Handlers
  // ============================================================================

  const handleGenerateSummary = async () => {
    if (!file?.url) {
      myToast.error("No file URL available");
      return;
    }

    const requestFileId = file.id;
    setIsGeneratingSummary(true);
    setCurrentRequestFileId(requestFileId);
    console.log("file summary agent :: ", agents);
    try {
      let fileSummaryAgent = agents.find((agent) =>
        agent.name?.toLowerCase().includes("filesummary")
      );
      console.log(
        "FileSummary Outputschema :: ",
        JSON.stringify(fileSummaryAgent.outputSchema, null, 2)
      );
      if (!fileSummaryAgent) {
        myToast.error("File Summary agent not found");
        setIsGeneratingSummary(false);
        return;
      }

      // Keep FileSummary config aligned with required instruction/schema.
      const needsFileSummaryAgentUpgrade =
        !String(fileSummaryAgent?.systemPreferences || "").includes(
          FILE_SUMMARY_AGENT_VERSION
        ) ||
        !fileSummaryAgent?.outputSchema;

      if (needsFileSummaryAgentUpgrade) {
        try {
          fileSummaryAgent = await apiUpdateAgent({
            id: fileSummaryAgent.id,
            systemPreferences: FILE_SUMMARY_SYSTEM_PREFERENCES,
            defaultPrompt: FILE_SUMMARY_DEFAULT_PROMPT,
            inputSchema: FILE_SUMMARY_INPUT_SCHEMA,
            inputSchemaType: "fileSummaryV2",
            outputSchema: FILE_SUMMARY_OUTPUT_SCHEMA,
          });
        } catch (agentUpdateError) {
          console.error(
            "Failed to update FileSummary agent config:",
            agentUpdateError
          );
          myToast.error("Failed to update FileSummary agent configuration");
          setIsGeneratingSummary(false);
          return;
        }
      }

      const { fileDocId, aiFileId } = await ensureAiFileId(file);
      if (!fileDocId) {
        myToast.error("Failed to get file document ID");
        setIsGeneratingSummary(false);
        return;
      }

      const isPDFFile = isPDF(file);
      if (isPDFFile) {
        myToast.info("Analyzing PDF... This may take a few minutes.");
      }

      myToast.info("Creating AI request...");

      // Build data array with available entities
      const dataArray = [{ type: "file", id: fileDocId }];
      if (propertyId) {
        dataArray.push({ type: "property", id: propertyId });
      }
      if (contactId) {
        dataArray.push({ type: "contact", id: contactId });
      }

      const createdRequest = await apiCreateAiRequest({
        authorId: PrivateInfo?.id,
        aiAgentId: fileSummaryAgent?.id,
        request: {},
      });

      const builtRequest = await aiSparkAiRequestBuilder({
        aiRequestId: createdRequest.id,
        data: dataArray,
        promptOverride: promptText || fileSummaryAgent.defaultPrompt,
      });

      const executedRequest = await apiExecuteAiRequestV2({
        requestId: builtRequest.id,
      });

      const completedRequest = await SparkUtilRequestBuilder.pollForCompletion(
        builtRequest.id,
        executedRequest,
        isPDFFile ? 600 : 300,
        3000
      );

      console.log(
        "[PreviewFile][Summary] completedRequest -> ",
        JSON.stringify(completedRequest, null, 2)
      );

      setAiRequestDebug((prev) => ({
        ...prev,
        summary: {
          ...(prev?.summary || {}),
          createdRequest,
          builtRequest,
          executedRequest,
          completedRequest,
        },
        aiRequestInput: {
          aiRequestId: builtRequest.id,
          data: dataArray,
          promptOverride: promptText || fileSummaryAgent.defaultPrompt,
        },
      }));

      setAiRequestDebug((prev) => ({
        ...prev,
        summary: {
          output: completedRequest,
        },
      }));

      console.log("summary completedRequest :: ", completedRequest);

      let summary = parseResponseText(completedRequest.response);
      console.log("summary :: ", summary);
      if (onSaveSummary) {
        onSaveSummary({
          summary: summary,
          prompt: promptText,
          aiFileID: completedRequest.aiFileIds?.[0] || fileDocId,
          fileId: requestFileId,
        });
      }

      if (requestFileId === file?.id) {
        setSummaryText(summary);
        myToast.success("Summary generated and saved!");
      }
    } catch (error) {
      console.error("Error generating summary:", error);
      myToast.error(
        "Failed to generate summary: " + (error.message || "Unknown error")
      );
    } finally {
      if (requestFileId === file?.id) {
        setIsGeneratingSummary(false);
      }
    }
  };

  const handleGenerateMetadata = async () => {
    if (!file?.url) {
      myToast.error("No file URL available");
      return;
    }

    const requestFileId = file.id;
    setIsGeneratingMetadata(true);
    setCurrentRequestFileId(requestFileId);

    try {
      const fileExtractAgent = agents.find(
        (agent) =>
          agent.name?.toLowerCase().includes("fileextract") ||
          agent.name?.toLowerCase().includes("file extract")
      );

      if (!fileExtractAgent) {
        myToast.error("FileExtract agent not found");
        setIsGeneratingMetadata(false);
        return;
      }

      if (!fileExtractAgent.defaultPrompt) {
        myToast.error("FileExtract agent is missing defaultPrompt");
        setIsGeneratingMetadata(false);
        return;
      }

      const { fileDocId, aiFileId } = await ensureAiFileId(file);
      if (!fileDocId) {
        myToast.error("Failed to get file document ID");
        setIsGeneratingMetadata(false);
        return;
      }

      const isPDFFile = isPDF(file);
      if (isPDFFile) {
        myToast.info(
          "Extracting text from PDF... This may take a few minutes."
        );
      }

      myToast.info("Creating AI request...");

      // Build data array with available entities
      const dataArray = [{ type: "file", id: fileDocId }];
      if (propertyId) {
        dataArray.push({ type: "property", id: propertyId });
      }
      if (contactId) {
        dataArray.push({ type: "contact", id: contactId });
      }

      // Build prompt - use custom metadata prompt if provided, otherwise use agent's default
      let promptText = fileExtractAgent.defaultPrompt;
      if (metadataPromptText && metadataPromptText.trim().length > 0) {
        promptText = metadataPromptText.trim();
      }

      // Use SparkUtilRequestBuilder to build and execute the request
      // This uses the backend metadata builder which properly processes files
      // PDFs can take much longer to process, so use a longer timeout (10 minutes)
      const executedRequest = await SparkUtilRequestBuilder.buildAndExecute({
        agentId: fileExtractAgent.id,
        authorId: PrivateInfo?.id,
        data: dataArray,
        overrides: {
          prompt: promptText,
          inputSchema: fileExtractAgent.inputSchema, // Use agent's configured inputSchema with placeholders
          schemaName: "TextExtraction",
        },
        pollMaxSeconds: isPDFFile ? 600 : 300, // 10 minutes for PDFs, 5 minutes for other files
      });

      console.log(
        "[PreviewFile][Metadata] executedRequest -> ",
        JSON.stringify(executedRequest, null, 2)
      );

      setAiRequestDebug((prev) => ({
        ...prev,
        metadata: {
          ...(prev?.metadata || {}),
          executedRequest,
        },
        aiRequestInput: executedRequest._debugInput,
      }));

      myToast.info("AI request executing...");

      let extractedText = parseExtractedText(executedRequest.response);

      if (onSaveSummary) {
        onSaveSummary({
          extractedMetadata: extractedText,
          metadataPrompt: metadataPromptText,
          aiFileID: executedRequest.aiFileIds?.[0] || fileDocId,
          fileId: requestFileId,
        });
      }

      if (requestFileId === file?.id) {
        setExtractedMetadata(extractedText);
        myToast.success("Text extraction complete and saved!");
      }
    } catch (error) {
      console.error("Error extracting text:", error);
      myToast.error(
        "Failed to extract text: " + (error.message || "Unknown error")
      );
    } finally {
      if (requestFileId === file?.id) {
        setIsGeneratingMetadata(false);
      }
    }
  };

  const handleExecuteOutputAgent = async () => {
    if (!file?.url) {
      myToast.error("No file URL available");
      return;
    }

    if (!selectedOutputAgentId) {
      myToast.error("Please select an AI Agent");
      return;
    }

    const selectedAgent = publicAgents.find(
      (agent) => agent.id === selectedOutputAgentId
    );

    if (!selectedAgent) {
      myToast.error("Selected agent not found");
      return;
    }

    setIsExecutingOutput(true);
    setOutputResponse(null);
    setOutputDownloadedFiles([]);

    try {
      if (!selectedAgent.defaultPrompt) {
        myToast.error(`Agent "${selectedAgent.name}" is missing defaultPrompt`);
        setIsExecutingOutput(false);
        return;
      }

      const { fileDocId, aiFileId } = await ensureAiFileId(file);
      if (!fileDocId) {
        myToast.error("Failed to get file document ID");
        setIsExecutingOutput(false);
        return;
      }

      const isPDFFile = isPDF(file);
      if (isPDFFile) {
        myToast.info("Processing PDF... This may take a few minutes.");
      }

      console.log("selected agent :: ", selectedAgent);

      myToast.info("Creating AI request...");

      // Build data array with available entities
      const dataArray = [{ type: "file", id: fileDocId }];
      if (propertyId) {
        dataArray.push({ type: "property", id: propertyId });
      }
      if (contactId) {
        dataArray.push({ type: "contact", id: contactId });
      }

      // Step 1: Create AI request with minimal request object
      const createdRequest = await apiCreateAiRequest({
        authorId: PrivateInfo?.id,
        aiAgentId: selectedAgent.id,
        request: {}, // Will be built by aiRequestSparkBuilder
      });

      if (!createdRequest?.id) {
        throw new Error("Failed to create AI request");
      }

      // Step 2: Build the AI request via aiRequestSparkBuilder (promptOverride supported)
      const promptOverride = outputPromptText || selectedAgent.defaultPrompt;
      const builtRequest = await aiSparkAiRequestBuilder({
        aiRequestId: createdRequest.id,
        data: dataArray,
        ...(promptOverride ? { promptOverride } : {}),
      });

      if (!builtRequest?.id) throw new Error("Failed to build AI request");

      // Step 3: Execute the AI request
      const executedRequest = await apiExecuteAiRequestV2({
        requestId: builtRequest.id,
      });

      if (!executedRequest?.id) {
        throw new Error("Failed to execute AI request");
      }

      // Poll for completion (PDFs can take longer)
      const pollMaxSeconds = isPDFFile ? 600 : 300; // 10 minutes for PDFs, 5 minutes for other files
      const pollIntervalMs = 2000;
      const maxAttempts = Math.ceil((pollMaxSeconds * 1000) / pollIntervalMs);
      const completedRequest = await pollForCompletion(
        builtRequest.id,
        executedRequest,
        maxAttempts
      );

      console.log("completedRequest ::: ", completedRequest);

      setAiRequestDebug((prev) => ({
        ...prev,
        output: {
          ...(prev?.output || {}),
          aiRequestInput: builtRequest,
          debug: completedRequest.debug,
          output: completedRequest,
        },
      }));

      setOutputResponse(completedRequest.response);

      const downloadedFileIds = completedRequest.downloadedFileIds || [];
      if (downloadedFileIds.length > 0) {
        try {
          const result = await apiGetFiles({ ids: downloadedFileIds });
          setOutputDownloadedFiles(result.files || []);
        } catch (fileError) {
          console.error("Error loading downloaded files:", fileError);
        }
      }

      myToast.success("AI execution completed!");
    } catch (error) {
      console.error("Error executing output agent:", error);
      myToast.error("Failed to execute: " + (error.message || "Unknown error"));
    } finally {
      setIsExecutingOutput(false);
    }
  };

  const handleClearOutput = () => {
    setOutputResponse(null);
    setOutputDownloadedFiles([]);
    setOutputPromptText("");
    myToast.info("Output cleared");
  };

  const handleClearSummary = () => {
    setSummaryText("");
    setPromptText("");
    if (onSaveSummary) {
      onSaveSummary({ summary: "", prompt: "" });
      myToast.success("Summary cleared and saved");
    } else {
      myToast.info("Summary cleared");
    }
  };

  const handleClearMetadata = () => {
    setExtractedMetadata("");
    setMetadataPromptText("");
    if (onSaveSummary) {
      onSaveSummary({
        summary: summaryText,
        extractedMetadata: "",
        prompt: promptText,
        metadataPrompt: "",
      });
      myToast.success("Metadata cleared and saved");
    } else {
      myToast.info("Metadata cleared");
    }
  };

  // ============================================================================
  // Polling and Response Parsing Helpers
  // ============================================================================

  const pollForCompletion = async (requestId, initialResponse, maxAttempts) => {
    let completedRequest = initialResponse;

    if (
      completedRequest.status !== "completed" &&
      completedRequest.status !== "failed"
    ) {
      let attempts = 0;

      while (attempts < maxAttempts) {
        attempts++;
        await new Promise((resolve) => setTimeout(resolve, 2000));

        const requestsResponse = await apiGetAiRequests({});
        const requests = requestsResponse?.aiRequests || [];
        const foundRequest = requests.find((r) => r.id === requestId);

        if (foundRequest) {
          completedRequest = foundRequest;

          if (foundRequest.status === "completed") {
            break;
          }

          if (foundRequest.status === "failed") {
            const errorMsg = foundRequest.response?.error || "Unknown error";
            throw new Error("AI request failed: " + errorMsg);
          }
        }
      }
    }

    if (completedRequest.status === "failed") {
      const errorMsg = completedRequest.response?.error || "Unknown error";
      throw new Error("AI request failed: " + errorMsg);
    }

    if (completedRequest.status !== "completed") {
      throw new Error(
        `AI request did not complete. Status: ${completedRequest.status}`
      );
    }

    return completedRequest;
  };

  const parseResponseText = (response) => {
    if (!response) return "";
    if (typeof response === "string") return response;
    if (response.text) return response.text;
    if (response.summary && typeof response.summary === "string") {
      const fileType = String(response.fileType || "").trim();
      const importantData = Array.isArray(response.importantData)
        ? response.importantData
            .map((row) => {
              const label = String(row?.label || "").trim();
              const value = String(row?.value || "").trim();
              const why = String(row?.whyItMatters || "").trim();
              const source = String(row?.source || "").trim();
              if (!label && !value && !why && !source) return "";
              const parts = [];
              if (label || value) parts.push(`${label || "Data"}: ${value || ""}`.trim());
              if (why) parts.push(`Why: ${why}`);
              if (source) parts.push(`Source: ${source}`);
              return `- ${parts.join(" | ")}`;
            })
            .filter(Boolean)
        : [];
      const keyRisks = Array.isArray(response.keyRisks)
        ? response.keyRisks
            .map((item) => String(item || "").trim())
            .filter(Boolean)
            .map((item) => `- ${item}`)
        : [];
      const followUps = Array.isArray(response.recommendedFollowUps)
        ? response.recommendedFollowUps
            .map((item) => String(item || "").trim())
            .filter(Boolean)
            .map((item) => `- ${item}`)
        : [];

      const sections = [];
      if (fileType) sections.push(`File Type: ${fileType}`);
      sections.push(response.summary.trim());
      if (importantData.length > 0) {
        sections.push(`Important Data:\n${importantData.join("\n")}`);
      }
      if (keyRisks.length > 0) {
        sections.push(`Key Risks:\n${keyRisks.join("\n")}`);
      }
      if (followUps.length > 0) {
        sections.push(`Recommended Follow-Ups:\n${followUps.join("\n")}`);
      }
      return sections.join("\n\n").trim();
    }
    return JSON.stringify(response);
  };

  const parseExtractedText = (response) => {
    if (!response) return "";
    if (typeof response === "string") return response;
    
    // Check for text field first
    if (response.text) return response.text;
    
    // Check for extractedData (without slash)
    if (response.extractedData) return response.extractedData;
    
    // Check for /extractedData (with leading slash - JSON Schema format)
    if (response["/extractedData"]) return response["/extractedData"];
    
    // If response is an object, check for common extraction field names
    if (typeof response === "object" && response !== null) {
      // Check for any key containing "extractedData" (case insensitive)
      const keys = Object.keys(response);
      for (const key of keys) {
        if (key.toLowerCase().includes("extracteddata") || key.toLowerCase().includes("extracted")) {
          const value = response[key];
          if (typeof value === "string") return value;
          if (value !== null && value !== undefined) return String(value);
        }
      }
      
      // If object has a single key, return its value (common pattern)
      if (keys.length === 1) {
        const value = response[keys[0]];
        if (typeof value === "string") return value;
        if (value !== null && value !== undefined) return String(value);
      }
    }
    
    // Fallback: stringify the response
    return JSON.stringify(response, null, 2);
  };

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        zIndex: 9999,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
        "& .MuiBackdrop-root": {
          zIndex: 9998,
        },
      }}
      PaperProps={{
        sx: {
          zIndex: 9999,
          margin: "auto",
        },
      }}
    >
      <DialogTitle
        sx={{
          py: 0,
          px: 0,
          backgroundColor: "#f3f4f6",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        {showAiRequestPreview ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              px: 1,
              minHeight: 44,
            }}
          >
            <IconButton
              size="small"
              onClick={() => setShowAiRequestPreview(false)}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 1,
              minHeight: 44,
            }}
          >
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="standard"
              textColor="primary"
              indicatorColor="primary"
              sx={{
                minHeight: 44,
                borderBottom: "1px solid #e5e7eb",
                "& .MuiTab-root": {
                  minHeight: 44,
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "0.9375rem",
                  px: 2,
                  py: 1,
                  color: "#6b7280",
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                  marginRight: 0.5,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#f9fafb",
                    color: "#374151",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "#ffffff",
                    color: "#1976d2",
                    border: "1px solid #e5e7eb",
                    borderBottom: "1px solid #ffffff",
                    marginBottom: "-1px",
                    zIndex: 1,
                  },
                },
                "& .MuiTabs-indicator": {
                  display: "none",
                },
              }}
            >
              <Tab label="View" />
              {!isSystemFile && <Tab label="Summary" />}
              {!isSystemFile && <Tab label="Metadata" />}
              {!isSystemFile && <Tab label="Output" />}
              {isSuperAdminRole && !isSystemFile && <Tab label="JSON" />}
              {isDomainFile && <Tab label="Info" />}
            </Tabs>

            {/* Tab Action Buttons */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {/* View Tab Actions */}
              {tab === 0 && (
                <IconButton
                  onClick={() => file && onDownload?.(file)}
                  size="small"
                  disabled={!file}
                >
                  <DownloadIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}

              {/* Summary Tab Actions */}
              {tab === SUMMARY_TAB && (
                <>
                  {/* {isSuperAdminRole && (
                    <Tooltip
                      title={
                        showAiRequestPreview ? "Back to Summary" : "AI Request"
                      }
                    >
                      <IconButton
                        size="small"
                        onClick={() => setShowAiRequestPreview((v) => !v)}
                        sx={{
                          color: "#2563eb",
                          "&:hover": { backgroundColor: "#dbeafe" },
                        }}
                      >
                        <InfoIcon sx={{ fontSize: 26 }} />
                      </IconButton>
                    </Tooltip>
                  )} */}
                  <Tooltip title={isFullScreenPrompt ? "Save" : "Edit"}>
                    <IconButton
                      size="small"
                      onClick={() => setIsFullScreenPrompt((v) => !v)}
                      sx={{
                        color: "#8b5cf6",
                        "&:hover": { backgroundColor: "#f1f5f9" },
                      }}
                    >
                      {isFullScreenPrompt ? (
                        <CloudUploadIcon sx={{ fontSize: 24 }} />
                      ) : (
                        <EditIcon sx={{ fontSize: 20 }} />
                      )}
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    size="small"
                    onClick={handleGenerateSummary}
                    disabled={isGeneratingSummary}
                    sx={{
                      color: "#ffffff",
                      backgroundColor: isGeneratingSummary
                        ? "#f59e0b"
                        : "#8b5cf6",
                      "&:hover": {
                        backgroundColor: isGeneratingSummary
                          ? "#d97706"
                          : "#7c3aed",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "#f59e0b",
                        color: "#ffffff",
                      },
                    }}
                  >
                    {isGeneratingSummary ? (
                      <CircularProgress size={14} sx={{ color: "#ffffff" }} />
                    ) : (
                      <AutoAwesomeIcon sx={{ fontSize: 16 }} />
                    )}
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={handleClearSummary}
                    disabled={isGeneratingSummary}
                    sx={{
                      color: "#64748b",
                      "&:hover": {
                        backgroundColor: "#fee2e2",
                        color: "#ef4444",
                      },
                      "&.Mui-disabled": { color: "#cbd5e1" },
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </>
              )}

              {/* Metadata Tab Actions */}
              {tab === METADATA_TAB && (
                <>
                  <Tooltip title={isEditingMetadata ? "Done" : "Edit"}>
                    <IconButton
                      size="small"
                      onClick={() => setIsEditingMetadata((v) => !v)}
                      sx={{
                        color: "#8b5cf6",
                        "&:hover": { backgroundColor: "#f1f5f9" },
                      }}
                    >
                      <EditIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    size="small"
                    onClick={handleGenerateMetadata}
                    disabled={isGeneratingMetadata}
                    sx={{
                      color: "#ffffff",
                      backgroundColor: isGeneratingMetadata
                        ? "#f59e0b"
                        : "#8b5cf6",
                      "&:hover": {
                        backgroundColor: isGeneratingMetadata
                          ? "#d97706"
                          : "#7c3aed",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "#f59e0b",
                        color: "#ffffff",
                      },
                    }}
                  >
                    {isGeneratingMetadata ? (
                      <CircularProgress size={14} sx={{ color: "#ffffff" }} />
                    ) : (
                      <AutoAwesomeIcon sx={{ fontSize: 16 }} />
                    )}
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={handleClearMetadata}
                    disabled={isGeneratingMetadata}
                    sx={{
                      color: "#64748b",
                      "&:hover": {
                        backgroundColor: "#fee2e2",
                        color: "#ef4444",
                      },
                      "&.Mui-disabled": { color: "#cbd5e1" },
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </>
              )}

              {/* Output Tab Actions */}
              {tab === 3 && !isSystemFile && (
                <>
                  <Tooltip
                    title={
                      showAiRequestPreview ? "Back to output" : "AI Request"
                    }
                  >
                    <IconButton
                      size="small"
                      onClick={() => setShowAiRequestPreview((v) => !v)}
                      sx={{
                        color: "#2563eb",
                        "&:hover": { backgroundColor: "#dbeafe" },
                      }}
                    >
                      <InfoIcon sx={{ fontSize: 26 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title={isEditingOutputPrompt ? "Done" : "Edit Prompt"}
                  >
                    <IconButton
                      size="small"
                      onClick={() => setIsEditingOutputPrompt((v) => !v)}
                      sx={{
                        color: "#8b5cf6",
                        "&:hover": { backgroundColor: "#f1f5f9" },
                      }}
                    >
                      <EditIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    size="small"
                    onClick={handleExecuteOutputAgent}
                    disabled={isExecutingOutput || !selectedOutputAgentId}
                    sx={{
                      color: "#ffffff",
                      backgroundColor: isExecutingOutput
                        ? "#f59e0b"
                        : "#8b5cf6",
                      "&:hover": {
                        backgroundColor: isExecutingOutput
                          ? "#d97706"
                          : "#7c3aed",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: selectedOutputAgentId
                          ? "#f59e0b"
                          : "#e2e8f0",
                        color: selectedOutputAgentId ? "#ffffff" : "#94a3b8",
                      },
                    }}
                  >
                    {isExecutingOutput ? (
                      <CircularProgress size={14} sx={{ color: "#ffffff" }} />
                    ) : (
                      <AutoAwesomeIcon sx={{ fontSize: 16 }} />
                    )}
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={handleClearOutput}
                    disabled={isExecutingOutput}
                    sx={{
                      color: "#64748b",
                      "&:hover": {
                        backgroundColor: "#fee2e2",
                        color: "#ef4444",
                      },
                      "&.Mui-disabled": { color: "#cbd5e1" },
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </>
              )}

              <IconButton onClick={onClose} size="small">
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Box>
        )}
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {showAiRequestPreview ? (
          <Box
            sx={{
              height: "75vh",
              backgroundColor: "#ffffff",
              p: 2,
              overflow: "auto",
            }}
          >
            {/* <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: "#1e293b" }}>
              AI Request Debug Information
            </Typography> */}

            {tab === SUMMARY_TAB && aiRequestDebug?.debug && (
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, fontWeight: 600, color: "#475569" }}
                >
                  Summary Ai Request
                </Typography>
                <TextField
                  value={JSON.stringify(
                    aiRequestDebug?.aiRequestInput,
                    null,
                    2
                  )}
                  multiline
                  fullWidth
                  disabled
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f8fafc",
                      fontFamily: "monospace",
                      fontSize: "13px",
                      "& fieldset": { borderColor: "#e2e8f0" },
                    },
                  }}
                />
              </Box>
            )}

            {tab === 3 && aiRequestDebug?.output?.debug && (
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, fontWeight: 600, color: "#475569" }}
                >
                  Complete AI Request
                </Typography>
                <TextField
                  value={JSON.stringify(aiRequestDebug?.output?.debug, null, 2)}
                  multiline
                  fullWidth
                  disabled
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f8fafc",
                      fontFamily: "monospace",
                      fontSize: "13px",
                      "& fieldset": { borderColor: "#e2e8f0" },
                    },
                  }}
                />
              </Box>
            )}

            {!aiRequestDebug?.summary &&
              !aiRequestDebug?.metadata &&
              !aiRequestDebug?.output && (
                <Typography
                  variant="body2"
                  sx={{ color: "#64748b", fontStyle: "italic" }}
                >
                  No AI request data available. Generate a request first.
                </Typography>
              )}
          </Box>
        ) : (
          <Box
            sx={{ display: "flex", flexDirection: "column", height: "75vh" }}
          >
            <Box sx={{ flex: 1, minHeight: 0 }}>
              {/* View Tab */}
              {tab === 0 && (
                <ViewTab
                  file={file}
                  fileContent={fileContent}
                  loadingFileContent={loadingFileContent}
                  onDownload={onDownload}
                />
              )}

              {/* Summary Tab */}
              {tab === SUMMARY_TAB && (
                <SummaryTab
                  summaryText={summaryText}
                  setSummaryText={setSummaryText}
                  promptText={promptText}
                  setPromptText={setPromptText}
                  isEditingSummary={isEditingSummary}
                  setIsEditingSummary={setIsEditingSummary}
                  isFullScreenPrompt={isFullScreenPrompt}
                  setIsFullScreenPrompt={setIsFullScreenPrompt}
                  isGeneratingSummary={isGeneratingSummary}
                  onSaveSummary={onSaveSummary}
                  file={file}
                />
              )}

              {/* Metadata Tab */}
              {tab === METADATA_TAB && (
                <MetadataTab
                  extractedMetadata={extractedMetadata}
                  setExtractedMetadata={setExtractedMetadata}
                  isEditingMetadata={isEditingMetadata}
                  setIsEditingMetadata={setIsEditingMetadata}
                  isGeneratingMetadata={isGeneratingMetadata}
                  metadataPairs={metadataPairs}
                />
              )}

              {/* Output Tab */}
              {tab === OUTPUT_TAB && !isSystemFile && (
                <OutputTab
                  publicAgents={publicAgents}
                  loadingAgents={loadingPublicAgents}
                  selectedOutputAgentId={selectedOutputAgentId}
                  setSelectedOutputAgentId={setSelectedOutputAgentId}
                  outputResponse={outputResponse}
                  outputDownloadedFiles={outputDownloadedFiles}
                  isExecutingOutput={isExecutingOutput}
                  isEditingOutputPrompt={isEditingOutputPrompt}
                  outputPromptText={outputPromptText}
                  setOutputPromptText={setOutputPromptText}
                  aiRequestDebug={aiRequestDebug?.output || null}
                />
              )}

              {/* JSON Tab */}
              {tab === JSON_TAB && isSuperAdminRole && !isSystemFile && (
                <Box
                  sx={{
                    height: "100%",
                    overflow: "auto",
                    backgroundColor: "#ffffff",
                    p: 2,
                  }}
                >
                  {/* <Typography
                    variant="caption"
                    sx={{
                      color: "#374151",
                      fontWeight: 600,
                      fontSize: "11px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      mb: 1.5,
                      display: "block",
                    }}
                  >
                    AI Request Debug
                  </Typography> */}

                  <Box
                    sx={{
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      backgroundColor: "#f5f3ff",
                      color: "#475569",
                      p: 2,
                      fontFamily: "monospace",
                      fontSize: "13.5px",
                      whiteSpace: "pre",
                      overflowX: "auto",
                    }}
                  >
                    {JSON.stringify(
                      {
                        file: {
                          id: file?.id,
                          name: file?.originalName || file?.name,
                          mimeType: file?.mimeType,
                          size: file?.size,
                          fileDocumentId: file?.fileDocumentId,
                        },
                        summary: aiRequestDebug?.summary || null,
                        backendFileDoc: backendFileDoc || null,
                        backendDocFetchedAt: backendDocFetchedAt || null,
                      },
                      null,
                      2
                    )}
                  </Box>
                </Box>
              )}

              {/* Info Tab */}
              {tab === INFO_TAB && isDomainFile && <InfoTab file={file} />}
            </Box>

            {/* Footer */}
            <Box
              sx={{
                borderTop: "1px solid #e5e7eb",
                backgroundColor: "#ffffff",
                px: 2,
                py: 1,
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "#64748b",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                <strong>Name: </strong> {bottomFileName}
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                <strong>Category:</strong> {file?.fileCategory || file?.type || ""}
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                <strong>File size:</strong>{" "}
                {file?.size ? `${(file.size / 1024).toFixed(2)} KB` : "—"}
              </Typography>
              {/* <Typography variant="caption" sx={{ color: "#64748b" }}>
                <strong>Uploaded At:</strong>{" "}
                {file?.uploadedAt 
                  ? moment(file?.uploadedAt).format("MMM DD, YYYY hh:mm A") 
                  : "—"}
              </Typography> */}
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                <strong>Updated At:</strong>{" "}
                {file?.updatedAt 
                  ? moment(file.updatedAt).format("MMM DD, YYYY hh:mm A") 
                  : "—"}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PreviewFile;
